import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { getSales, PunctualityInsight, RejectionInsight } from 'apis/merchant-gateway/insights';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { CustomLegends, Legend } from 'modules/experience-core';
import {
  formatGrowthRate,
  formatPercentage,
  formatSeconds,
  getGrowthRateForFields,
  getGrowthTrend,
} from 'modules/insights-v2/utils';

import { DEFAULT_PUNCTUALITY, DEFAULT_REJECTION } from './constants';
import { InsightResponseWithPreviousInterval } from '../../types';
import MetricBlock from '../OverallPerformance/MetricBlock';

type OperationsSummaryProps = {
  rejectionsInsight: InsightResponseWithPreviousInterval<RejectionInsight>;
  punctualityInsight: InsightResponseWithPreviousInterval<PunctualityInsight>;
  currency?: Currency;
  legends: Legend[];
  isVATEnabled: boolean;
  hidePunctualityAndPrepTime?: boolean;
  showHybridDeliveryDisclaimer?: boolean;
};

const OperationsSummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-block: 16px;
  padding-inline: 24px;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    padding: 0;
  }
`;

const OperationsSummarySection = styled.div`
  display: flex;
  gap: 48px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 32px;
    padding: 24px;
  }
`;

const OperationsSummary = ({
  rejectionsInsight,
  punctualityInsight,
  currency,
  legends,
  isVATEnabled,
  hidePunctualityAndPrepTime,
  showHybridDeliveryDisclaimer,
}: OperationsSummaryProps) => {
  const rejectionTotalForSelected = rejectionsInsight.selected.data?.data?.rejections?.total ?? DEFAULT_REJECTION;
  const rejectionTotalForPrevious = rejectionsInsight.previous.data?.data?.rejections?.total ?? DEFAULT_REJECTION;

  const punctualityTotalForSelected = punctualityInsight.selected.data?.data?.punctuality?.total ?? DEFAULT_PUNCTUALITY;
  const punctualityTotalForPrevious = punctualityInsight.previous.data?.data?.punctuality?.total ?? DEFAULT_PUNCTUALITY;

  const { formatMessage, formatNumber, locale } = useIntl();
  const rejectionGrowthRates = getGrowthRateForFields(rejectionTotalForSelected, rejectionTotalForPrevious, [
    'orders',
    'salesNoVat',
    'salesWithVat',
  ]);

  const punctualityGrowthRates = getGrowthRateForFields(punctualityTotalForSelected, punctualityTotalForPrevious, [
    'punctuality',
    'avgPreparationTimeSec',
  ]);

  return (
    <OperationsSummaryContainer data-test-id="test-operations-summary">
      <OperationsSummarySection>
        <MetricBlock
          title={formatMessage({ id: 'insights.sales-by-venue.rejections' })}
          tooltip={formatMessage({ id: 'insights.rejections.tooltip' })}
          metrics={rejectionTotalForSelected.orders}
          growth={formatGrowthRate(rejectionGrowthRates.orders, locale)}
          growthTrend={getGrowthTrend(rejectionGrowthRates.orders, true)}
        />
        <MetricBlock
          title={formatMessage({ id: 'insights.operations.loss-from-rejections' })}
          tooltip={formatMessage({ id: 'insights.operations.loss-from-rejections-tooltip' })}
          metrics={formatNumber(getSales(rejectionTotalForSelected, isVATEnabled), { currency, style: 'currency' })}
          growth={formatGrowthRate(getSales(rejectionGrowthRates, isVATEnabled), locale)}
          growthTrend={getGrowthTrend(getSales(rejectionGrowthRates, isVATEnabled), true)}
        />
        {!hidePunctualityAndPrepTime && (
          <>
            <MetricBlock
              title={formatMessage({ id: 'insights.punctuality.title' })}
              tooltip={
                showHybridDeliveryDisclaimer
                  ? formatMessage({ id: 'insights.punctuality.tooltip-hybrid-delivery' })
                  : formatMessage({ id: 'insights.punctuality.tooltip' })
              }
              metrics={formatPercentage(punctualityTotalForSelected.punctuality, locale)}
              growth={formatGrowthRate(punctualityGrowthRates.punctuality, locale)}
              growthTrend={getGrowthTrend(punctualityGrowthRates.punctuality)}
            />
            <MetricBlock
              title={formatMessage({ id: 'insights.operations.preparation-time' })}
              tooltip={
                showHybridDeliveryDisclaimer
                  ? formatMessage({ id: 'insights.preparation-time.tooltip-hybrid-delivery' })
                  : formatMessage({ id: 'insights.preparation-time.tooltip' })
              }
              metrics={formatSeconds(punctualityTotalForSelected.avgPreparationTimeSec)}
              growth={formatGrowthRate(punctualityGrowthRates.avgPreparationTimeSec, locale)}
              growthTrend={getGrowthTrend(punctualityGrowthRates.avgPreparationTimeSec, true)}
            />
          </>
        )}
      </OperationsSummarySection>
      <OperationsSummarySection>
        <CustomLegends legends={legends} />
      </OperationsSummarySection>
    </OperationsSummaryContainer>
  );
};

export default OperationsSummary;
